// The server can only guess a visitor's time zone from their IP address. When the
// page marks the value as that guess, the device clock wins: this finds the picker
// row that matches the browser's IANA zone, or an alias of it (Asia/Calcutta vs
// Asia/Kolkata), so the visitor sees the zone they are actually in.

export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch {
    return null
  }
}

export const canonicalZone = (iana) => {
  try {
    return Intl.DateTimeFormat(undefined, { timeZone: iana }).resolvedOptions()
      .timeZone
  } catch {
    return null
  }
}

export const matchBrowserZone = (ianas, local = browserTimeZone()) => {
  if (!local) return null
  if (ianas.includes(local)) return local
  const canonical = canonicalZone(local)
  if (!canonical) return null
  return (
    ianas.find(
      (iana) => iana === canonical || canonicalZone(iana) === canonical
    ) || null
  )
}

// A zone the visitor picked is remembered per browser, together with the device zone at the time.
// It only applies while the device is still in that zone: a laptop that has since moved on was
// picked for a trip that is over, and the normal order (device, then the IP guess) takes over.
export const PICK_KEY = 'simplero:booking-time-zone'

export const rememberedZone = (stored, ianas, device) => {
  if (!stored || !device || stored.device !== device) return null
  return ianas.includes(stored.iana) ? stored.iana : null
}

// A pick from the last few minutes is still on the visitor's mind; an older one deserves a louder reminder.
export const RECENT_PICK_MS = 15 * 60 * 1000
export const pickIsRecent = (stored, now = Date.now()) =>
  !!stored?.at && now - stored.at < RECENT_PICK_MS

// Even touching localStorage can throw (blocked storage in a third-party embed, Safari private mode),
// so it is resolved inside the guard: a failed read or write means "not remembered".
export const readPick = (storage) => {
  try {
    return (
      JSON.parse((storage ?? globalThis.localStorage).getItem(PICK_KEY)) || null
    )
  } catch {
    return null
  }
}

export const writePick = (iana, device, storage, at = Date.now()) => {
  try {
    ;(storage ?? globalThis.localStorage).setItem(
      PICK_KEY,
      JSON.stringify({ iana, device, at })
    )
  } catch {
    // not remembered, nothing else to do
  }
}
